import { Query } from '@apollo/client/react/components';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { ErrorHandler } from '@playbookux/playbook-web-core';
import { PROJECT_TYPES_ENUMS } from '../../../../core/constants/projects';
import { PARTICIPANT_MENU_ITEMS } from '../../../../core/constants/approval-participant-menu-items';
import DEVICE_TYPES from '../../../../core/constants/device-types';
import PARTICIPANT_SOURCES from '../../../../core/constants/participantsSource';
import Completed from './completed';
import Expired from './expired-testers';
import InviteTesters from './invite-testers';
import PendingTesters from './pending-testers';
import RecruitingLinks from './recruiting-links';
import Redirect from './redirect/index';
import Status from './status';
import Integrations from './integrations';

import {
  Item, Loader, Menu, MenuContainer,
} from './styles';

import { GET_TESTERS, GET_RECRUITING_LINKS } from '../graphql';
import { LIMIT, QUALIFIED_STATUS } from './constants';
import { TestSessionProvider } from '../test-session-context-provider';

const ApprovalParticipant = (props) => {
  const {
    project = {},
    refetch,
    onSetCurrentTester,
  } = props;
  const {
    id,
    participantsSource,
    segments,
    testType,
  } = project;
  const [activeMenuItem, setActiveMenuItem] = useState(PARTICIPANT_MENU_ITEMS.INVITE_MORE_TESTERS);
  const [showLoadMoreButton, setShowLoadMoreButton] = useState(true);

  const handleOnClickMenuItem = (event, { name }) => {
    setActiveMenuItem(name);
  };

  const handleRenderMenuItemContent = () => {
    if (activeMenuItem === PARTICIPANT_MENU_ITEMS.INVITE_MORE_TESTERS) {
      return (
        <Query
          query={GET_TESTERS}
          variables={{
            approved: false,
            projectId: id,
            removed: false,
            status: QUALIFIED_STATUS,
            limit: LIMIT,
            offset: 0,
          }}
        >
          {({
            loading, errors, data = {}, fetchMore,
          }) => {
            if (loading) {
              return <Loader size="small" data-loader="invite-more-tester-menu-loader" />;
            }

            if (errors) {
              return (
                <ErrorHandler
                  error={errors}
                  defaultMessage="Error while retrieving projects."
                />
              );
            }

            const { testers = [] } = data;

            if (testers.length < LIMIT && showLoadMoreButton) {
              setShowLoadMoreButton(false);
            }

            return (
              <InviteTesters
                onClickLoadMore={() => fetchMore({
                  variables: {
                    limit: LIMIT,
                    offset: testers.length,
                  },
                  updateQuery: (prev, { fetchMoreResult }) => {
                    if (fetchMoreResult.testers.length < LIMIT) {
                      setShowLoadMoreButton(false);
                    }

                    return {
                      ...prev,
                      testers: [...prev.testers, ...fetchMoreResult.testers],
                    };
                  },
                })}
                showLoadMoreButton={showLoadMoreButton}
                testers={testers}
                project={project}
                refetch={refetch}
              />
            );
          }}
        </Query>
      );
    }

    if (activeMenuItem === PARTICIPANT_MENU_ITEMS.PENDING_TESTERS) {
      return (
        <Query
          fetchPolicy="network-only"
          query={GET_TESTERS}
          variables={{
            isPending: true,
            status: QUALIFIED_STATUS,
            projectId: id,
            isModerated: testType === PROJECT_TYPES_ENUMS.MODERATED,
          }}
        >
          {({ loading, errors, data = {} }) => {
            if (loading) {
              return <Loader size="small" data-loader="pending-tester-menu-loader" />;
            }

            if (errors) {
              return (
                <ErrorHandler
                  error={errors}
                  defaultMessage="Error while retrieving the testers."
                />
              );
            }
            const { testers } = data;
            return (
              <PendingTesters
                projectId={id}
                testers={testers}
                onSetCurrentTester={onSetCurrentTester}
                refetch={refetch}
              />
            );
          }}
        </Query>
      );
    }

    if (activeMenuItem === PARTICIPANT_MENU_ITEMS.COMPLETED) {
      return (
        <>
          <TestSessionProvider projectId={id}>
            <Completed
              onSetCurrentTester={onSetCurrentTester}
              testType={project.testType}
              refetch={refetch}
              projectId={id}
            />
          </TestSessionProvider>
        </>

      );
    }

    if (activeMenuItem === PARTICIPANT_MENU_ITEMS.EXPIRED) {
      return (
        <Expired
          projectId={id}
          onSetCurrentTester={onSetCurrentTester}
          testType={project.testType}
          refetch={refetch}
        />
      );
    }

    if (activeMenuItem === PARTICIPANT_MENU_ITEMS.RECRUITING_LINK) {
      return (
        <Query
          query={GET_RECRUITING_LINKS}
          variables={{
            projectId: id,
          }}
        >
          {({ loading, error, data = {} }) => {
            if (loading) {
              return <Loader size="small" data-loader="recruit-link-menu-loader" />;
            }

            if (error) {
              return (
                <ErrorHandler
                  error={error}
                  defaultMessage="Error while retrieving the links."
                />
              );
            }
            const {
              project: { segments: segmentsData },
            } = data;

            return segmentsData.map((segment) => (
              <RecruitingLinks
                project={project}
                segment={segment}
                refetch={refetch}
              />
            ));
          }}
        </Query>
      );
    }

    if (activeMenuItem === PARTICIPANT_MENU_ITEMS.STATUS) {
      return (
        <Status
          project={project}
          refetch={refetch}
        />
      );
    }

    if (activeMenuItem === PARTICIPANT_MENU_ITEMS.INTEGRATIONS) {
      return (
        <Integrations project={project} refetch={refetch} />
      );
    }

    if (activeMenuItem === PARTICIPANT_MENU_ITEMS.REDIRECT) {
      return <Redirect project={project} refetch={refetch} />;
    }

    return null;
  };

  const hasComputerStudy = segments.some(
    (segment) => segment.device === DEVICE_TYPES.COMPUTER,
  );

  const isUnmoderated = testType === PROJECT_TYPES_ENUMS.UNMODERATED;

  return (
    <MenuContainer>
      <Menu pointing secondary>
        <Item
          name={PARTICIPANT_MENU_ITEMS.INVITE_MORE_TESTERS}
          active={activeMenuItem === PARTICIPANT_MENU_ITEMS.INVITE_MORE_TESTERS}
          onClick={handleOnClickMenuItem}
        />
        <Item
          name={PARTICIPANT_MENU_ITEMS.PENDING_TESTERS}
          active={activeMenuItem === PARTICIPANT_MENU_ITEMS.PENDING_TESTERS}
          onClick={handleOnClickMenuItem}
        />
        <Item
          name={PARTICIPANT_MENU_ITEMS.COMPLETED}
          active={activeMenuItem === PARTICIPANT_MENU_ITEMS.COMPLETED}
          onClick={handleOnClickMenuItem}
        />
        <Item
          name={PARTICIPANT_MENU_ITEMS.EXPIRED}
          active={activeMenuItem === PARTICIPANT_MENU_ITEMS.EXPIRED}
          onClick={handleOnClickMenuItem}
        />
        <Item
          name={PARTICIPANT_MENU_ITEMS.RECRUITING_LINK}
          active={activeMenuItem === PARTICIPANT_MENU_ITEMS.RECRUITING_LINK}
          onClick={handleOnClickMenuItem}
        />
        {(participantsSource === PARTICIPANT_SOURCES.PLAYBOOK || (hasComputerStudy && isUnmoderated))
          && (
            <Item
              name={PARTICIPANT_MENU_ITEMS.STATUS}
              active={activeMenuItem === PARTICIPANT_MENU_ITEMS.STATUS}
              onClick={handleOnClickMenuItem}
            />
          )}

        <Item
          name={PARTICIPANT_MENU_ITEMS.INTEGRATIONS}
          active={activeMenuItem === PARTICIPANT_MENU_ITEMS.INTEGRATIONS}
          onClick={handleOnClickMenuItem}
        />

        {(testType === PROJECT_TYPES_ENUMS.CARD_SORT || testType === PROJECT_TYPES_ENUMS.TREE_TEST
          || testType === PROJECT_TYPES_ENUMS.SURVEY)
          && (
            <Item
              name={PARTICIPANT_MENU_ITEMS.REDIRECT}
              active={activeMenuItem === PARTICIPANT_MENU_ITEMS.REDIRECT}
              onClick={handleOnClickMenuItem}
            />
          )}
      </Menu>

      {handleRenderMenuItemContent()}
    </MenuContainer>
  );
};

ApprovalParticipant.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.string,
    testType: PropTypes.string,
    participantsSource: PropTypes.string,
    segments: PropTypes.arrayOf(
      PropTypes.shape({
        recorderType: PropTypes.string,
      }),
    ),
  }).isRequired,
  onSetCurrentTester: PropTypes.func.isRequired,
  refetch: PropTypes.func.isRequired,
};

export default ApprovalParticipant;
